import React, { useState, MouseEvent } from 'react';
import BaseTab from './BaseTab';
import BaseMenuItem from './BaseMenuItem';
import Divider from '@mui/material/Divider';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import * as Features from '../../../../../../shared/Features';
import history from '../../../../../../shared/history';
import Menu from '@mui/material/Menu';
import { Translate } from '../../../../../../shared/LanguageService';
import { TranslatedLabels } from '../../../../../../shared/types';
import { useUserContext } from '../../../../../../shared/context/user-context';

type EconomyTabProps = {
    label: string;
};

type EconomyMenuProps = {
    labels: TranslatedLabels;
} & TranslatedEconomyMenuProps;

type TranslatedEconomyMenuProps = {
    open: boolean;
    anchorElement: HTMLButtonElement | null;
    onClose: () => void;
};

const InvoiceRoute = '/invoice';
const InvoicesRoute = '/invoices';
const PhotographerInvoiceRoute = '/photographer-invoice';
const InvoiceJournalsRoute = '/invoice-journals';
const PaymentJournalsRoute = '/payment-journals';
const ImportExportRoute = '/importexport';
const InvoiceProductTypesRoute = '/invoice-product-types';
const ReceivablesRoute = '/receivables';
const ExportToProcountorRoute = '/export-to-procountor';

let routes = [
    InvoiceRoute,
    InvoicesRoute,
    PhotographerInvoiceRoute,
    InvoiceJournalsRoute,
    PaymentJournalsRoute,
    ImportExportRoute,
    InvoiceProductTypesRoute,
    ReceivablesRoute,
    ExportToProcountorRoute
];

function EconomyMenu({ open, onClose, anchorElement, labels }: EconomyMenuProps) {
    const { user } = useUserContext();

    const shouldShowCreateInvoices = Features.hasFeature(Features.CreateInvoices, user);
    const shouldShowViewInvoices = Features.hasFeature(Features.ViewInvoices, user);
    const shouldShowCreatePayments = Features.hasFeature(Features.CreatePayments, user);
    const shouldShowListInvoiceJournals = Features.hasFeature(Features.ListInvoiceJournals, user);
    const shouldShowListPaymentJournals = Features.hasFeature(Features.ListPaymentJournals, user);
    const shouldShowViewAllPhotographerInvoices = Features.hasFeature(
        Features.ViewAllPhotographerInvoices,
        user
    );
    const shouldShowListInvoiceProductTypes = Features.hasFeature(
        Features.ListInvoiceProductType,
        user
    );
    const shouldShowViewReceivable = Features.hasFeature(Features.ViewReceivable, user);

    const shouldShowDividerAfterInvoicing =
        (shouldShowCreateInvoices || shouldShowViewInvoices || shouldShowCreatePayments) &&
        (shouldShowListInvoiceJournals ||
            shouldShowListPaymentJournals ||
            shouldShowViewAllPhotographerInvoices ||
            shouldShowListInvoiceProductTypes ||
            shouldShowViewReceivable);

    const shouldShowDividerAfterJournals =
        (shouldShowListInvoiceJournals || shouldShowListPaymentJournals) &&
        (shouldShowViewAllPhotographerInvoices ||
            shouldShowListInvoiceProductTypes ||
            shouldShowViewReceivable);

    const shouldShowExportToProcountor = Features.hasFeature(Features.ExportToProcountor, user);
    const shouldShowDividerZentuvoInvoicing = shouldShowExportToProcountor;

    function handleClickCreateInvoices() {
        onClose();
        history.push(InvoiceRoute);
    }

    function handleClickViewInvoices() {
        onClose();
        history.push(InvoicesRoute);
    }

    function handleClickViewAllPhotographerInvoices() {
        onClose();
        history.push(PhotographerInvoiceRoute);
    }

    function handleClickListInvoiceJournals() {
        onClose();
        history.push(InvoiceJournalsRoute);
    }

    function handleClickListPaymentJournals() {
        onClose();
        history.push(PaymentJournalsRoute);
    }

    function handleClickCreatePayments() {
        onClose();
        history.push(ImportExportRoute);
    }

    function handleClickListInvoiceProductTypes() {
        onClose();
        history.push(InvoiceProductTypesRoute);
    }

    function handleClickViewReceivable() {
        onClose();
        history.push(ReceivablesRoute);
    }

    function handleClickExportToProcountor() {
        onClose();
        history.push(ExportToProcountorRoute);
    }
    
    return (
        <Menu anchorEl={anchorElement} open={open} onClose={onClose}>
            {shouldShowCreateInvoices ? (
                <BaseMenuItem
                    route={InvoiceRoute}
                    onClick={handleClickCreateInvoices}
                    label={labels.createInvoices}
                />
            ) : null}
            {shouldShowViewInvoices ? (
                <BaseMenuItem
                    route={InvoicesRoute}
                    onClick={handleClickViewInvoices}
                    label={labels.viewInvoices}
                />
            ) : null}
            {shouldShowCreatePayments ? (
                <BaseMenuItem
                    route={ImportExportRoute}
                    onClick={handleClickCreatePayments}
                    label={labels.createPayments}
                />
            ) : null}
            {shouldShowDividerAfterInvoicing ? <Divider /> : null}
            {shouldShowListInvoiceJournals ? (
                <BaseMenuItem
                    route={InvoiceJournalsRoute}
                    onClick={handleClickListInvoiceJournals}
                    label={labels.listInvoiceJournals}
                />
            ) : null}
            {shouldShowListPaymentJournals ? (
                <BaseMenuItem
                    route={PaymentJournalsRoute}
                    onClick={handleClickListPaymentJournals}
                    label={labels.listPaymentJournals}
                />
            ) : null}
            {shouldShowDividerAfterJournals ? <Divider /> : null}
            {shouldShowViewAllPhotographerInvoices ? (
                <BaseMenuItem
                    route={PhotographerInvoiceRoute}
                    onClick={handleClickViewAllPhotographerInvoices}
                    label={labels.viewAllPhotographerInvoices}
                />
            ) : null}
            {shouldShowListInvoiceProductTypes ? (
                <BaseMenuItem
                    route={InvoiceProductTypesRoute}
                    onClick={handleClickListInvoiceProductTypes}
                    label={labels.listInvoiceProductTypes}
                />
            ) : null}
            {shouldShowViewReceivable ? (
                <BaseMenuItem
                    route={ReceivablesRoute}
                    onClick={handleClickViewReceivable}
                    label={labels.viewReceivable}
                />
            ) : null}
            {shouldShowDividerZentuvoInvoicing ? <Divider /> : null}
            {shouldShowExportToProcountor ? (
                <BaseMenuItem
                    route={ExportToProcountorRoute}
                    onClick={handleClickExportToProcountor}
                    label={labels.exportToProcountor}
                />
            ) : null}            
        </Menu>
    );
}

function TranslatedEconomyMenu(props: TranslatedEconomyMenuProps) {
    const labels: TranslatedLabels = {
        createInvoices: Translate('header/nav/invoice'),
        viewInvoices: Translate('header/nav/invoices'),
        viewAllPhotographerInvoices: Translate('header/nav/photographer-invoice'),
        listInvoiceJournals: Translate('header/nav/invoice-journals'),
        listPaymentJournals: Translate('header/nav/payment-journals'),
        createPayments: Translate('header/nav/import-payments'),
        listInvoiceProductTypes: Translate('header/nav/invoice-product-types'),
        viewReceivable: Translate('header/nav/view-receivable'),
        exportToProcountor: Translate('header/nav/export-to-procountor')
    };

    return <EconomyMenu {...props} labels={labels} />;
}

function EconomyTab({ label, ...props }: EconomyTabProps) {
    const [showMenu, setShowMenu] = useState<boolean>(false);
    const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(null);

    function handleClick(event: MouseEvent<HTMLButtonElement>) {
        setAnchorElement(event.currentTarget);
        setShowMenu((oldShowMenu) => !oldShowMenu);
    }

    function handleCloseMenu() {
        setShowMenu(false);
    }

    return (
        <>
            <BaseTab
                {...props}
                icon={
                    showMenu ? (
                        <ExpandLessIcon sx={{ width: '24px', height: '24px' }} />
                    ) : (
                        <ExpandMoreIcon sx={{ width: '24px', height: '24px' }} />
                    )
                }
                iconPosition="end"
                onClick={handleClick}
                label={label}
                sx={{ minHeight: '48px', height: '48px' }}
                route={null}
            />
            <TranslatedEconomyMenu
                open={showMenu}
                anchorElement={anchorElement}
                onClose={handleCloseMenu}
            />
        </>
    );
}

function TranslatedEconomyTab(props) {
    const label = Translate('header/nav/dropdown/economy-menu');

    return <EconomyTab {...props} label={label} />;
}

TranslatedEconomyTab.routes = routes;

export default TranslatedEconomyTab;
